import { AlertTriangle, Bell, ChartNoAxesCombined, CirclePlus, CircleUserRound, FileCheck2, LayoutDashboard, ListOrdered, LogOut, Menu, Settings2, Target, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { PortfolioSummaryDto } from '../../shared/types';
import { api } from '../api';
import { pageTitleForPath } from '../navigation';
import { buildNotificationItems } from '../notifications';

type Props = {
  user: { displayName: string; pictureUrl: string | null; role: string };
  summary: PortfolioSummaryDto | null;
  children: ReactNode;
};

const links = [
  { to: '/', label: 'ภาพรวม', icon: LayoutDashboard, end: true },
  { to: '/upload', label: 'เพิ่มข้อมูล', icon: CirclePlus },
  { to: '/transactions', label: 'รายการ', icon: ListOrdered },
  { to: '/targets', label: 'เป้าหมาย', icon: Target },
  { to: '/settings', label: 'ตั้งค่า', icon: Settings2 },
];

export function Shell({ user, summary, children }: Props) {
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const notificationItems = summary ? buildNotificationItems(summary.health) : [];
  useEffect(() => { setOpen(false); setNotificationsOpen(false); setProfileOpen(false); }, [pathname]);
  async function signOut() {
    await api('/auth/logout', { method: 'POST' });
    location.reload();
  }
  return <div className="app-shell">
    <aside className={`sidebar ${open ? 'sidebar-open' : ''}`}>
      <div className="brand">
        <div className="brand-mark"><ChartNoAxesCombined size={22} /></div>
        <div><strong>Private Wealth</strong><span>Investment tracker</span></div>
        <button className="mobile-close icon-button" onClick={() => setOpen(false)} aria-label="ปิดเมนู"><X /></button>
      </div>
      <nav className="side-nav">
        <span className="nav-kicker">WORKSPACE</span>
        {links.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}>
          <Icon size={19} /><span>{label}</span>
        </NavLink>)}
      </nav>
      <div className="side-foot">
        <button className="sidebar-alert-button" onClick={() => { setNotificationsOpen((value) => !value); setProfileOpen(false); setOpen(false); }} aria-expanded={notificationsOpen}><Bell size={17} /> การแจ้งเตือน{notificationItems.length > 0 && <b>{notificationItems.length}</b>}</button>
        <div className="privacy-chip"><span className="pulse-dot" /> ข้อมูลส่วนตัวของคุณเท่านั้น</div>
        <button className="user-card" onClick={() => navigate('/settings')}>
          {user.pictureUrl ? <img src={user.pictureUrl} alt="" /> : <CircleUserRound size={34} />}
          <span><strong>{user.displayName}</strong><small>{user.role === 'ADMIN' ? 'ผู้ดูแลระบบ' : 'สมาชิก'}</small></span>
          <Settings2 size={17} />
        </button>
        <button className="text-button signout" onClick={signOut}><LogOut size={16} /> ออกจากระบบ</button>
      </div>
    </aside>
    {open && <button className="backdrop" onClick={() => setOpen(false)} aria-label="ปิดเมนู" />}
    <div className="main-column">
      <header className="mobile-topbar">
        <button className="icon-button" onClick={() => setOpen(true)} aria-label="เปิดเมนู"><Menu /></button>
        <strong>{pageTitleForPath(pathname)}</strong>
        <div className="mobile-topbar-actions">
          <button className="icon-button notification-trigger" onClick={() => { setNotificationsOpen((value) => !value); setProfileOpen(false); }} aria-label="เปิดการแจ้งเตือน" aria-expanded={notificationsOpen}><Bell size={18} />{notificationItems.length > 0 && <b>{notificationItems.length}</b>}</button>
          <button className="mobile-top-avatar" onClick={() => { setProfileOpen((value) => !value); setNotificationsOpen(false); }} aria-label="เปิดเมนูโปรไฟล์" aria-expanded={profileOpen}>{user.pictureUrl ? <img src={user.pictureUrl} alt="" /> : user.displayName.slice(0, 1)}</button>
        </div>
      </header>
      <main className="page">{children}</main>
    </div>
    {(notificationsOpen || profileOpen) && <button className="popover-backdrop" onClick={() => { setNotificationsOpen(false); setProfileOpen(false); }} aria-label="ปิดแผง" />}
    {notificationsOpen && <aside className="notification-popover" aria-label="การแจ้งเตือน">
      <div className="popover-head"><span><strong>การแจ้งเตือน</strong><small>รายการที่ควรตรวจสอบ</small></span><button className="icon-button" onClick={() => setNotificationsOpen(false)} aria-label="ปิดการแจ้งเตือน"><X size={17} /></button></div>
      <div className="notification-list">{notificationItems.map((item) => <Link to={item.to} key={item.kind} onClick={() => setNotificationsOpen(false)}><span className="notification-icon">{item.kind === 'DRAFT' ? <FileCheck2 /> : <AlertTriangle />}</span><span><strong>{item.label}</strong><small>{item.detail}</small></span></Link>)}{notificationItems.length === 0 && <div className="notification-empty"><Bell /><strong>ไม่มีรายการที่ต้องดูแล</strong><span>ข้อมูลพอร์ตและสลิปเป็นปกติ</span></div>}</div>
      <Link className="popover-link" to="/targets" onClick={() => setNotificationsOpen(false)}>จัดการแจ้งเตือนราคา</Link>
    </aside>}
    {profileOpen && <aside className="profile-popover" aria-label="เมนูโปรไฟล์">
      <div className="profile-popover-user">{user.pictureUrl ? <img src={user.pictureUrl} alt="" /> : <CircleUserRound />}<span><strong>{user.displayName}</strong><small>{user.role === 'ADMIN' ? 'ผู้ดูแลระบบ' : 'สมาชิก'}</small></span></div>
      <div className="profile-privacy"><span className="pulse-dot" /> ข้อมูลส่วนตัวของคุณเท่านั้น</div>
      <button onClick={() => navigate('/settings')}><Settings2 size={16} /> ตั้งค่า</button>
      <button onClick={signOut}><LogOut size={16} /> ออกจากระบบ</button>
    </aside>}
    <nav className="bottom-nav">
      {links.slice(0, 4).map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end}>
        <Icon size={20} /><span>{label}</span>
      </NavLink>)}
    </nav>
  </div>;
}
